import { useState } from 'react';

// Renders a product/book description string with Amazon-like formatting.
// Splits on blank lines into blocks; bullet blocks (lines starting with •, *, -)
// become lists; short heading-like lines become subheadings; everything else
// is a paragraph.

const isBulletLine = (line) => /^\s*[•*\-–]\s+/.test(line);
const stripBullet = (line) => line.replace(/^\s*[•*\-–]\s+/, '');

const looksLikeHeading = (block) => {
  if (block.includes('\n')) return false;
  const t = block.trim();
  if (t.length === 0 || t.length > 80) return false;
  if (/[.?!:]$/.test(t)) return false;
  return true;
};

const renderBlock = (block, idx) => {
  const lines = block.split('\n').filter((l) => l.trim().length > 0);
  if (lines.length > 0 && lines.every(isBulletLine)) {
    return (
      <ul key={idx} className="list-disc pl-6 my-3 space-y-1.5 text-black">
        {lines.map((l, i) => (
          <li key={i} >{stripBullet(l)}</li>
        ))}
      </ul>
    );
  }
  if (lines.some(isBulletLine)) {
    // Mixed: split into paragraph + list segments.
    const segments = [];
    let buffer = [];
    let bulletBuffer = [];
    const flushPara = () => {
      if (buffer.length) {
        segments.push({ type: 'p', text: buffer.join(' ') });
        buffer = [];
      }
    };
    const flushList = () => {
      if (bulletBuffer.length) {
        segments.push({ type: 'ul', items: bulletBuffer.slice() });
        bulletBuffer = [];
      }
    };
    lines.forEach((l) => {
      if (isBulletLine(l)) {
        flushPara();
        bulletBuffer.push(stripBullet(l));
      } else {
        flushList();
        buffer.push(l);
      }
    });
    flushPara();
    flushList();
    return (
      <div key={idx} className="my-2">
        {segments.map((seg, i) =>
          seg.type === 'ul' ? (
            <ul key={i} className="list-disc pl-6 my-3 space-y-1.5 text-black">
              {seg.items.map((it, j) => (
                <li key={j} className="leading-7">{it}</li>
              ))}
            </ul>
          ) : (
            <p key={i} className="text-black leading-7 my-2">{seg.text}</p>
          ),
        )}
      </div>
    );
  }
  if (looksLikeHeading(block)) {
    return (
      <h3 key={idx} className="text-base font-bold text-black mt-5 mb-2">
        {block.trim()}
      </h3>
    );
  }
  return (
    <p key={idx} className="text-black leading-7 my-2">
      {block.trim()}
    </p>
  );
};

const ProductDescription = ({ text, collapsible = false, previewLength = 180 }) => {
  const [expanded, setExpanded] = useState(false);

  if (!text || !text.trim()) {
    return <p className="text-sm text-black">No description available.</p>;
  }
  const normalizedText = text.trim().replace(/\r\n/g, '\n');
  const blocks = normalizedText.split(/\n\s*\n/);
  const needsToggle = collapsible && normalizedText.length > previewLength;
  const preview = needsToggle
    ? `${normalizedText.slice(0, previewLength).trimEnd()}…`
    : normalizedText;

  if (collapsible && !expanded) {
    return (
      <div className="text-sm">
        <p className="font-semibold text-black leading-6 whitespace-pre-line">{preview}</p>
        {needsToggle && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="mt-5 text-cyan-700 hover:text-orange-600 hover:underline"
          >
            read more
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="text-sm">
      {blocks.map(renderBlock)}
      {needsToggle && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="mt-3 text-cyan-700 hover:text-orange-600 hover:underline"
        >
          read less
        </button>
      )}
    </div>
  );
};

export default ProductDescription;
